import React, { useEffect, useState } from "react";
import { getData } from "../utilities/UtilityFunctions";
import {
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Grid,
  LinearProgress,
  Typography,
  useTheme
} from "@mui/material";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import { userDisplayName } from "../../pages/events/components/EventFunctions";
import { BCPAngleRight } from "../icons/bcp/BCPIcons";

const IS_GW =
  getEnvironmentVariable("IS_GW") === "true" ||
  getEnvironmentVariable("COMPANY_FLAG") === "gw";

export default function LeagueRankingCard(props) {
  const {
    disablePhotoFetch = true,
    buttonLabel,
    region,
    regionId,
    onClick,
    placingsType,
    leagueId,
    href
  } = props;

  const [photo, setPhoto] = useState(null);
  const [ranking, setRanking] = useState(props.ranking);
  const [subLoading, setSubLoading] = useState(!ranking?.leaderboards);
  const theme = useTheme();

  const fetchLeaderboard = async () => {
    try {
      if (!ranking.league) return;

      setSubLoading(true);

      const params = {
        placingsType: placingsType
          ? placingsType
          : ranking?.league?.isArmy
            ? "army"
            : "player",
        limit: 5,
        leagueId: leagueId || ranking.league.id,
        sortAscending: false
      };

      if (ranking?.league?.isFaction) {
        params.armyId = ranking?.league?.id;
      }

      if (region || regionId) {
        params.regionId = regionId || region.id;
      }

      const apiLeaderboard = await getData("/placings", params);

      setRanking((prevRanking) => ({
        ...prevRanking,
        leaderboards: apiLeaderboard?.data || []
      }));
    } catch (err) {
    } finally {
      setSubLoading(false);
    }
  };

  const fetchPhoto = async () => {
    try {
      const apiPhoto = await getData(`/files/${ranking.league.photoFileId}`);
      if (apiPhoto) {
        setPhoto(apiPhoto);
      }
    } catch {}
  };

  useEffect(() => {
    if (ranking) {
      ranking.leaderboards = [];

      // If no leaderboards, fetch them and add them to the ranking object
      fetchLeaderboard();
    }

    if (ranking?.league?.photoFileId && !disablePhotoFetch) fetchPhoto();
    // eslint-disable-next-line
  }, [region, regionId]);

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between"
      }}
    >
      <CardActionArea
        onClick={onClick}
        href={onClick ? "" : href ? href : `/rankings/${ranking?.league?.id}`}
        target="_blank"
      >
        <CardMedia
          component="img"
          image={
            ranking?.league?.photo?.url ||
            ranking?.league?.photoUrl ||
            photo?.url ||
            ranking?.league?.imageUrl ||
            ranking?.league?.gameSystem?.url ||
            `${getEnvironmentVariable(
              "SYSTEM_IMAGES_CLOUDFRONT"
            )}/images/EventImage${
              theme?.palette?.mode === "dark" ? "Dark" : ""
            }.png`
          }
          alt={ranking?.league?.name}
          sx={{
            backgroundColor: "#222222",
            maxHeight: "calc((100vw - 24px) * 0.5625)",
            "@media (min-width: 600px)": {
              maxHeight: "calc((100vw - 24px) * 0.5 * 0.5625)"
            },
            "@media (min-width: 900px)": {
              maxHeight: "calc((100vw - 24px) * 0.3333 * 0.5625)"
            },
            "@media (min-width: 1218px)": {
              maxHeight: "215px"
            }
          }}
        />

        <CardHeader
          title={
            <>
              <Typography
                variant="h4"
                s
                sx={{
                  m: 0,
                  fontWeight: "700",
                  fontSize: "20px",
                  lineHeight: "1.334"
                }}
              >
                {ranking?.league?.name}
              </Typography>
              <Typography
                gutterBottom={ranking?.league?.active}
                sx={{
                  fontWeight: "400",
                  fontSize: "18px",
                  lineHeight: "1.334"
                }}
              >
                {ranking?.league?.gameSystem?.name}
              </Typography>
              {!ranking?.league?.active && (
                <Typography
                  gutterBottom
                  sx={{
                    fontWeight: "400",
                    fontSize: "18px",
                    lineHeight: "1.334",
                    color: "error.main"
                  }}
                >
                  {`(Inactive)`}
                </Typography>
              )}
            </>
          }
        />
      </CardActionArea>

      <CardContent>
        <Grid container spacing={1}>
          {/* Loop through rankings here */}
          {ranking?.league?.hidePlacings ? (
            <Grid item xs={12}>
              <Typography>Rankings are hidden</Typography>
            </Grid>
          ) : (
            <>
              {subLoading ? (
                <Grid item xs={12}>
                  <LinearProgress />
                </Grid>
              ) : ranking?.leaderboards?.length ? (
                ranking?.leaderboards?.map((rank, index) => {
                  if (index >= 5) return <></>;

                  return (
                    <CardActionArea
                      href={
                        rank?.user?.id
                          ? `/user/${rank?.user?.id}${leagueId || ranking?.league?.id ? `?league=${leagueId || ranking?.league?.id}` : ""}`
                          : rank?.team?.id
                            ? `/team/${rank?.team?.id}${leagueId || ranking?.league?.id ? `?league=${leagueId || ranking?.league?.id}` : ""}`
                            : ""
                      }
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        pt: 0.5,
                        pb: 0.5,
                        gap: 1,
                        borderRadius: IS_GW ? 0 : 1,
                        backgroundColor: IS_GW
                          ? index % 2 === 0
                            ? "table.evenRowBackground"
                            : "table.oddRowBackground"
                          : "inherit",
                        borderBottom: IS_GW ? "1px solid" : "none",
                        borderTop: IS_GW && index === 0 ? "1px solid" : "none"
                      }}
                      key={rank?.user?.id || rank?.team?.id || index}
                      target={IS_GW ? "_blank" : ""}
                    >
                      <Grid item xs={2} sx={{ textAlign: "left", pl: 1 }}>
                        <Typography>{rank?.placing}</Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography
                          sx={{
                            color:
                              (rank?.user?.id || rank?.team?.id) && !IS_GW
                                ? "link.main"
                                : "inherit",
                            overflow: "hidden"
                          }}
                        >
                          {rank?.user?.firstName && rank?.user?.lastName
                            ? userDisplayName(
                                rank?.user?.firstName,
                                rank?.user?.lastName
                              )
                            : rank?.team?.name || "-----"}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography sx={{ textAlign: "right", pr: 1 }}>
                          {rank?.ELOPoints !== undefined
                            ? `${rank?.ELOPoints}`
                            : rank?.totalPoints
                              ? Math.round(
                                  (rank?.totalPoints + Number.EPSILON) * 100
                                ) / 100
                              : rank?.ITCPoints
                                ? Math.round(
                                    (rank?.ITCPoints + Number.EPSILON) * 100
                                  ) / 100
                                : "-"}
                        </Typography>
                      </Grid>
                    </CardActionArea>
                  );
                })
              ) : (
                <Grid item xs={12}>
                  <Typography>No rankings available</Typography>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </CardContent>

      {IS_GW ? (
        <Grid
          item
          xs={12}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 1
          }}
        >
          <Button
            onClick={onClick}
            href={
              onClick ? "" : href ? href : `/rankings/${ranking?.league?.id}`
            }
            target="_blank"
            variant="contained"
            endIcon={<BCPAngleRight sx={{ fontSize: "14px", width: "14px" }} />}
            sx={{ borderRadius: 50 }}
          >
            Complete Leaders
          </Button>
        </Grid>
      ) : (
        <CardActionArea
          onClick={onClick}
          href={onClick ? "" : href ? href : `/rankings/${ranking?.league?.id}`}
          target="_blank"
        >
          <CardActions>
            <Button
              fullWidth
              sx={{
                color: "link.main",
                textTransform: "none"
              }}
            >
              {buttonLabel || "View complete leaderboard..."}
            </Button>
          </CardActions>
        </CardActionArea>
      )}
    </Card>
  );
}
